import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Share2, 
  Check, 
  BarChart3, 
  Activity, 
  Heart, 
  Zap, 
  Target,
  Calendar,
  TrendingUp,
  Trophy,
  Star,
  Sparkles
} from 'lucide-react';
import { useTranslation } from '../providers/TranslationProvider';
import { useAuth } from '../contexts/AuthContext';
import { useUI } from '../hooks/useUI';
import { useUnifiedStats } from '../hooks/useUnifiedStats';
import { SCREENS } from '../utils/constants';

/**
 * UnifiedStatisticsScreen - iOS 26 Dopamine-Optimized Design
 * Futuristic statistics sharing interface
 */
const UnifiedStatisticsScreen = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentScreen, screenParams, navigateToScreen, addFeedItem } = useUI();
  const { stats, isLoading, error } = useUnifiedStats();
  const [activeTab, setActiveTab] = useState('overview');
  
  // Check if this is share mode (called from IdentityFeed)
  const isShareMode = screenParams?.isShareMode || (screenParams?.privacy && screenParams?.contentType === 'stats');
  const [selectedStats, setSelectedStats] = useState([]);
  const [isSharing, setIsSharing] = useState(false);

  // Statistics Categories with iOS 26 design
  const statisticCategories = [
    {
      id: 'overview',
      title: t('identityCircle.feed.generalLevelXP') || 'Genel Seviye ve XP',
      icon: Trophy,
      gradient: 'from-yellow-500 to-orange-600',
      stats: stats ? [
        { 
          label: t('identityCircle.feed.generalLevel') || 'Genel Seviye', 
          value: stats.general?.level || 12, 
          unit: '', 
          change: 8,
          target: 15,
          color: 'from-yellow-400 to-orange-500'
        },
        { 
          label: t('identityCircle.feed.generalXP') || 'Genel XP', 
          value: stats.general?.xp || 2640, 
          unit: 'XP', 
          change: 15,
          target: 3000,
          color: 'from-orange-400 to-red-500'
        }
      ] : []
    },
    {
      id: 'basic',
      title: t('identityCircle.feed.basicStatistics') || 'Temel İstatistikler',
      icon: BarChart3,
      gradient: 'from-blue-500 to-cyan-600',
      stats: stats ? [
        { 
          label: t('identityCircle.feed.activeDays') || 'Aktif Gün', 
          value: stats.basic?.activeDays || 45, 
          unit: t('identityCircle.feed.days') || 'gün', 
          change: 12,
          target: 60,
          color: 'from-blue-400 to-cyan-500'
        },
        { 
          label: t('identityCircle.feed.totalTasks') || 'Toplam Görev', 
          value: stats.basic?.totalTasks || 23, 
          unit: '', 
          change: 8,
          target: 30,
          color: 'from-cyan-400 to-teal-500'
        }
      ] : []
    },
    {
      id: 'achievements',
      title: t('identityCircle.feed.achievements') || 'Başarımlar',
      icon: Star,
      gradient: 'from-purple-500 to-pink-600',
      stats: stats ? [
        { 
          label: t('identityCircle.feed.earnedBadges') || 'Kazanılan Rozet', 
          value: stats.achievements?.badges || 7, 
          unit: t('identityCircle.feed.badges') || 'rozet', 
          change: 40,
          target: 10,
          color: 'from-purple-400 to-pink-500'
        },
        { 
          label: t('identityCircle.feed.completedGoals') || 'Tamamlanan Hedef', 
          value: stats.achievements?.milestones || 3, 
          unit: t('identityCircle.feed.goals') || 'hedef', 
          change: 50,
          target: 5,
          color: 'from-pink-400 to-rose-500'
        }
      ] : []
    },
    {
      id: 'qr',
      title: t('identityCircle.feed.qrStatistics') || 'QR İstatistikleri',
      icon: Activity,
      gradient: 'from-emerald-500 to-green-600',
      stats: stats ? [
        { 
          label: t('identityCircle.feed.totalQR') || 'Toplam QR', 
          value: stats.qrStats?.totalQRs || 15, 
          unit: '', 
          change: 25,
          target: 20,
          color: 'from-emerald-400 to-green-500'
        },
        { 
          label: t('identityCircle.feed.qrViews') || 'QR Görüntüleme', 
          value: stats.qrStats?.totalViews || 127, 
          unit: '', 
          change: 18,
          target: 150,
          color: 'from-green-400 to-lime-500'
        }
      ] : []
    },
    {
      id: 'health',
      title: t('identityCircle.feed.health') || 'Sağlık',
      icon: Heart,
      gradient: 'from-red-500 to-pink-600',
      stats: stats ? [
        { 
          label: t('identityCircle.feed.averageSleep') || 'Ortalama Uyku', 
          value: stats.healthStats?.sleepAvg || 7.2, 
          unit: t('identityCircle.feed.hours') || 'saat', 
          change: 5,
          target: 8,
          color: 'from-red-400 to-pink-500'
        },
        { 
          label: t('identityCircle.feed.dailySteps') || 'Günlük Adım', 
          value: stats.healthStats?.steps || 8429, 
          unit: '', 
          change: 12,
          target: 10000,
          color: 'from-pink-400 to-rose-500'
        }
      ] : []
    },
    {
      id: 'productivity',
      title: t('identityCircle.feed.productivity') || 'Üretkenlik',
      icon: Zap,
      gradient: 'from-indigo-500 to-purple-600',
      stats: stats ? [
        { 
          label: t('identityCircle.feed.focusTime') || 'Odaklanma Süresi', 
          value: stats.productivityStats?.focusMinutes || 240, 
          unit: t('identityCircle.feed.minutes') || 'dk', 
          change: 20,
          target: 300,
          color: 'from-indigo-400 to-purple-500'
        },
        { 
          label: t('identityCircle.feed.completedTasks') || 'Tamamlanan Görev', 
          value: stats.productivityStats?.tasksCompleted || 18, 
          unit: '', 
          change: 15,
          target: 25,
          color: 'from-purple-400 to-violet-500'
        }
      ] : []
    }
  ];

  // Toggle stat selection for sharing
  const toggleStatSelection = useCallback((categoryId, stat) => {
    if (!isShareMode) return;
    
    const statKey = `${categoryId}-${stat.label}`;
    setSelectedStats(prev => {
      const isSelected = prev.some(s => s.key === statKey);
      if (isSelected) {
        return prev.filter(s => s.key !== statKey);
      } else {
        return [...prev, { 
          key: statKey, 
          categoryId, 
          stat: { ...stat },
          categoryTitle: statisticCategories.find(c => c.id === categoryId)?.title
        }];
      }
    });
  }, [isShareMode, statisticCategories]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (isShareMode) {
      // Go back to IdentityFeed
      navigateToScreen(SCREENS.IDENTITY_CIRCLE);
    } else {
      // Go back to previous screen
      navigateToScreen(SCREENS.IDENTITY_CIRCLE);
    }
  }, [isShareMode, navigateToScreen]);

  // Share selected statistics
  const handleShareStats = useCallback(async () => {
    if (selectedStats.length === 0) return;
    
    setIsSharing(true);
    
    try {
      // Create statistics content for feed
      const statsContent = {
        id: Date.now(),
        type: 'stats',
        user: {
          name: user?.name || 'You',
          profilePhoto: user?.profilePhoto,
          bio: user?.bio || 'Statistics enthusiast',
          location: user?.location,
          joinDate: user?.joinDate || 'Recently',
          stats: { posts: 0, connections: 0, followers: 0 }
        },
        content: `İlerleme raporumu paylaşıyorum! 📈`,
        statistics: {
          title: selectedStats.length > 1 ? 'İstatistiklerim' : selectedStats[0].categoryTitle,
          metrics: selectedStats.map(item => ({
            label: item.stat.label,
            value: item.stat.value.toString(),
            unit: item.stat.unit || ''
          }))
        },
        timestamp: new Date(),
        likes: 0,
        comments: [],
        shares: 0,
        privacy: screenParams.privacy
      };

      // Add to feed
      if (addFeedItem) {
        addFeedItem(statsContent);
      }

      // Navigate back to identity circle
      navigateToScreen(SCREENS.IDENTITY_CIRCLE);
      
    } catch (error) {
      console.error('Error sharing statistics:', error);
    } finally {
      setIsSharing(false);
    }
  }, [selectedStats, user, screenParams, addFeedItem, navigateToScreen]);

  // Handle sharing
  const handleShare = useCallback(async () => {
    if (selectedStats.length === 0 || isSharing) return;

    setIsSharing(true);

    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1500));      // Create feed post
      const newPost = {
        id: Date.now(),
        type: 'stats',
        user: {
          name: user?.name || 'You',
          profilePhoto: user?.profilePhoto || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face&auto=format&q=80',
          bio: user?.bio || 'Shared statistics',
          location: user?.location || 'Location not set',
          joinDate: user?.joinDate || 'Recently',
          stats: { posts: 1, connections: 0, followers: 0 }
        },
        content: t('identityCircle.feed.sharingProgress') || 'İlerleme raporumu paylaşıyorum! 📈',
        statistics: {
          title: t('identityCircle.feed.statisticsReport') || 'İstatistik Raporu',
          metrics: selectedStats.map(stat => ({
            label: stat.label,
            value: stat.value,
            unit: stat.unit || ''
          }))
        },
        timestamp: new Date(),
        likes: 0,
        comments: [],
        shares: 0
      };

      // Add to feed
      addFeedItem(newPost);

      // Navigate back to feed
      navigateToScreen(SCREENS.IDENTITY_CIRCLE);
    } catch (error) {
      console.error('Failed to share stats:', error);
    } finally {
      setIsSharing(false);
    }
  }, [selectedStats, isSharing, user, screenParams, addFeedItem, navigateToScreen]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <motion.div
          className="text-white text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="w-16 h-16 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
          <p>İstatistikler yükleniyor...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-white text-center">
          <p className="text-red-400">Hata: {error}</p>
          <button 
            onClick={handleBack}
            className="mt-4 px-6 py-2 bg-blue-500 rounded-lg"
          >
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-20"
          animate={{
            background: [
              'radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%)',
              'radial-gradient(circle at 80% 20%, rgba(147, 51, 234, 0.3) 0%, transparent 50%)',
              'radial-gradient(circle at 40% 80%, rgba(236, 72, 153, 0.3) 0%, transparent 50%)'
            ]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Header */}
      <motion.div 
        className="relative z-10 flex items-center justify-between p-6 backdrop-blur-xl bg-slate-800/40 border-b border-white/10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center space-x-4">
          <motion.button
            className="w-10 h-10 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleBack}
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.button>
          
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">
                {isShareMode ? 'İstatistik Paylaş' : 'İstatistikler'}
              </h1>
              <p className="text-sm text-white/60">
                {isShareMode ? 'İlerlemeni seç ve paylaş' : 'Tüm aktivitelerinin genel görünümü'}
              </p>
            </div>
          </div>
        </div>

        {isShareMode && (
          <motion.button
            className={`px-6 py-3 rounded-2xl font-semibold transition-all flex items-center space-x-2 ${
              selectedStats.length > 0
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg hover:shadow-blue-500/25'
                : 'bg-white/10 text-white/50 cursor-not-allowed'
            }`}
            whileHover={selectedStats.length > 0 ? { scale: 1.05, y: -2 } : {}}
            whileTap={selectedStats.length > 0 ? { scale: 0.95 } : {}}
            onClick={handleShareStats}
            disabled={selectedStats.length === 0 || isSharing}
          >
            {isSharing ? (
              <>
                <motion.div
                  className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                />
                <span>{t('identityCircle.feed.sharing') || 'Paylaşılıyor...'}</span>
              </>
            ) : (
              <>
                <Share2 className="w-4 h-4" />
                <span>{t('identityCircle.feed.shareStatsButton') || 'Paylaş'} ({selectedStats.length})</span>
              </>
            )}
          </motion.button>
        )}
      </motion.div>

      {/* Statistics Categories */}
      <div className="relative z-10 p-6 space-y-6">
        {statisticCategories.map((category, index) => {
          const IconComponent = category.icon;
          
          return (
            <motion.div
              key={category.id}
              className="bg-gradient-to-br from-slate-800/60 via-slate-700/40 to-slate-900/60 backdrop-blur-xl rounded-3xl p-6 border border-white/10 shadow-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              {/* Category Header */}
              <div className="flex items-center space-x-3 mb-6">
                <div className={`w-12 h-12 bg-gradient-to-r ${category.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                  <IconComponent className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{category.title}</h3>
                  <p className="text-sm text-white/60">{category.stats.length} metrik</p>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {category.stats.map((stat, statIndex) => {
                  const isSelected = selectedStats.some(s => s.key === `${category.id}-${stat.label}`);
                  
                  return (
                    <motion.div
                      key={statIndex}
                      className={`relative bg-white/5 rounded-2xl p-4 border transition-all cursor-pointer ${
                        isShareMode 
                          ? isSelected 
                            ? 'border-blue-500 bg-blue-500/10' 
                            : 'border-white/10 hover:border-white/20'
                          : 'border-white/10'
                      }`}
                      whileHover={{ scale: isShareMode ? 1.02 : 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => isShareMode && toggleStatSelection(category.id, stat)}
                    >
                      {/* Selection Indicator */}
                      {isShareMode && (
                        <div className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 transition-all ${
                          isSelected 
                            ? 'bg-blue-500 border-blue-500' 
                            : 'border-white/30'
                        }`}>
                          {isSelected && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="w-full h-full flex items-center justify-center"
                            >
                              <Check className="w-3 h-3 text-white" />
                            </motion.div>
                          )}
                        </div>
                      )}

                      <div className="flex justify-between items-center mb-3">
                        <span className="text-white/80 text-sm font-medium">{stat.label}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-white font-bold text-lg">
                            {stat.value} {stat.unit}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            stat.change > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                          }`}>
                            {stat.change > 0 ? '+' : ''}{stat.change}%
                          </span>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-white/20 rounded-full h-2 mb-2">
                        <motion.div
                          className={`bg-gradient-to-r ${stat.color} h-2 rounded-full`}
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min((stat.value / stat.target) * 100, 100)}%` }}
                          transition={{ duration: 1, delay: index * 0.1 + statIndex * 0.1 }}
                        />
                      </div>

                      <div className="flex justify-between text-xs text-white/50">
                        <span>0</span>
                        <span>Hedef: {stat.target}</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Share Mode Instructions */}
      {isShareMode && (
        <motion.div
          className="fixed bottom-6 left-6 right-6 bg-gradient-to-r from-blue-500/90 to-purple-600/90 backdrop-blur-xl rounded-2xl p-4 border border-white/20 z-50"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <div className="flex items-center space-x-3">
            <Sparkles className="w-5 h-5 text-white" />
            <p className="text-white text-sm">
              {selectedStats.length === 0 
                ? t('identityCircle.feed.selectStatsToShare') || 'Paylaşmak istediğin istatistikleri seç'
                : `${selectedStats.length} ${t('identityCircle.feed.shareStatsReady') || 'istatistik seçildi, paylaşmaya hazır!'}`
              }
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default UnifiedStatisticsScreen;
